// Post-Fast Insights Toast — overlay UI.
//
// Persistent, non-modal, dismissible toast that appears 20s after the user
// breaks a >9h fast. Rendered at App root so it sits above any tab. The
// in-flight Gemini call is owned by hayahFastToast's module-level registry,
// so dismissing this component does NOT cancel it — the result still writes
// to hayahFastEvents and dispatches sirat:fastUpdated.

import React, { useEffect, useMemo, useState } from "react";
import { ensureInsightFor, healthTiersAchieved } from "./hayahFastToast";

function fmtDuration(min) {
  if (!Number.isFinite(min) || min <= 0) return "0h 00m";
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function fmtClockTime(ms) {
  if (!Number.isFinite(ms)) return "—";
  try {
    const d = new Date(ms);
    const h = d.getHours();
    const m = d.getMinutes();
    const ap = h >= 12 ? "PM" : "AM";
    const hh = h % 12 || 12;
    return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
  } catch { return "—"; }
}

function fmtDayLabel(ms, anchor) {
  if (!Number.isFinite(ms)) return "";
  try {
    const d = new Date(ms);
    const a = new Date(anchor);
    const sameDay = d.toDateString() === a.toDateString();
    if (sameDay) return "Today";
    const y = new Date(a); y.setDate(y.getDate() - 1);
    if (d.toDateString() === y.toDateString()) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch { return ""; }
}

export default function HayahFastInsightToast({ open, event, dismiss }) {
  const [insight, setInsight] = useState(null);
  const [insightStatus, setInsightStatus] = useState("idle"); // idle | pending | done

  // Reset and (re)fetch the insight whenever the open event changes.
  useEffect(() => {
    if (!open || !event) {
      setInsight(null);
      setInsightStatus("idle");
      return;
    }
    if (event.ai_insight_cached) {
      setInsight(event.ai_insight_cached);
      setInsightStatus("done");
      return;
    }
    let cancelled = false;
    setInsightStatus("pending");
    ensureInsightFor(event).then(text => {
      if (cancelled) return;
      setInsight(text);
      setInsightStatus("done");
    }).catch(() => {
      // ensureInsightFor catches internally — defensive only.
      if (cancelled) return;
      setInsightStatus("done");
    });
    return () => { cancelled = true; };
  }, [open, event]);

  const tiers = useMemo(
    () => (event ? healthTiersAchieved(event.duration_min) : []),
    [event]
  );

  if (!open || !event) return null;

  const start = event.start_ts;
  const end   = event.end_ts;
  const startName  = event.starting_meal?.name || "Last meal";
  const breakName  = event.breaking_meal?.name || "First meal";

  return (
    <div className="fast-toast" role="dialog" aria-live="polite" aria-label="Fast complete insight">
      <div className="fast-toast__scroll">
        <div className="fast-toast__header">
          <div className="fast-toast__eyebrow">Fast complete</div>
          <div className="fast-toast__headline">{fmtDuration(event.duration_min)}</div>
        </div>

        <div className="fast-toast__summary">
          <div className="fast-toast__leg">
            <div className="fast-toast__leg-label">Started</div>
            <div className="fast-toast__leg-time">{fmtClockTime(start)}</div>
            <div className="fast-toast__leg-meta">{fmtDayLabel(start, end)} · {startName}</div>
          </div>
          <div className="fast-toast__arrow" aria-hidden>→</div>
          <div className="fast-toast__leg">
            <div className="fast-toast__leg-label">Broken</div>
            <div className="fast-toast__leg-time">{fmtClockTime(end)}</div>
            <div className="fast-toast__leg-meta">{fmtDayLabel(end, end)} · {breakName}</div>
          </div>
        </div>

        {tiers.length > 0 && (
          <div className="fast-toast__section">
            <div className="fast-toast__section-title">Health benefits achieved</div>
            <div className="fast-toast__tiers">
              {tiers.map(t => (
                <div key={t.slug} className="fast-toast__tier">
                  <div className="fast-toast__tier-title">{t.title}</div>
                  <ul className="fast-toast__tier-points">
                    {t.points.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="fast-toast__section">
          <div className="fast-toast__section-title">AI insight</div>
          {insightStatus === "pending" ? (
            <div className="fast-toast__insight fast-toast__insight--pending">Generating insight…</div>
          ) : (
            <pre className="fast-toast__insight">{insight || "—"}</pre>
          )}
        </div>
      </div>

      <button
        type="button"
        className="fast-toast__close"
        onClick={dismiss}
        aria-label="Close fast insight"
      >
        Close
      </button>
    </div>
  );
}
